import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { supabase } from '../lib/supabaseClient';
import Swal from 'sweetalert2';

export default function InsertQuickReview() {
  const [outlineOptions, setOutlineOptions] = useState([]);
  const [selectedOutline, setSelectedOutline] = useState(null);
  const [quickReviewHtml, setQuickReviewHtml] = useState('');
  const [status, setStatus] = useState('');

  // 🔄 Fetch all outline options once
  useEffect(() => {
    const fetchOutlines = async () => {
      const { data, error } = await supabase
        .from('topics')
        .select(`
          id,
          name,
          quick_review_html,
          parent:parent_id ( module_code )
        `)
        .eq('level', 3)
        .order('name');

      if (error) {
        console.error('Error fetching outlines:', error);
        setStatus('❌ Error loading outlines.');
        return;
      }

      const options = data.map((outline) => ({
        value: outline.id,
        label: `${outline.parent?.module_code || '❓'} - ${outline.name}`,
        quick_review_html: outline.quick_review_html || '',
      }));

      setOutlineOptions(options);
    };

    fetchOutlines();
  }, []);

  // 🎯 Populate HTML field on selection
  useEffect(() => {
    if (selectedOutline) {
      setQuickReviewHtml(selectedOutline.quick_review_html || '');
      setStatus('');
    } else {
      setQuickReviewHtml('');
    }
  }, [selectedOutline]);

  // 💾 Save HTML to Supabase
  const handleSave = async () => {
    if (!selectedOutline || !quickReviewHtml.trim()) {
      setStatus('❌ Please select an outline and enter review HTML.');
      return;
    }

    const { error } = await supabase
      .from('topics')
      .update({ quick_review_html: quickReviewHtml.trim() })
      .eq('id', selectedOutline.value);

    if (error) {
      console.error('Error saving quick review HTML:', error);
      Swal.fire('❌ Error', 'Failed to save quick review.', 'error');
    } else {
      Swal.fire('✅ Success', 'Quick Review HTML updated successfully!', 'success');
      setStatus('✅ Quick Review saved.');
    }
  };

  // 🔄 Fetch latest data from Supabase for selected outline
  const handleFetchLatest = async () => {
    if (!selectedOutline) return;

    const { data, error } = await supabase
      .from('topics')
      .select('quick_review_html')
      .eq('id', selectedOutline.value)
      .single();

    if (error) {
      console.error('Fetch error:', error);
      Swal.fire('❌ Error', 'Failed to fetch latest quick review.', 'error');
    } else {
      setQuickReviewHtml(data.quick_review_html || '');
      setStatus('🔄 Refreshed from database.');
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h2 className="text-2xl font-bold mb-6 text-indigo-700 text-center">⚡ Insert Quick Review HTML</h2>

      {/* Outline Selector */}
      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-700">Select Outline:</label>
        <Select
          options={outlineOptions}
          value={selectedOutline}
          onChange={setSelectedOutline}
          placeholder="Search outline..."
        />
      </div>

      {/* HTML Input */}
      <div className="mb-4">
        <label className="block mb-1 font-medium text-gray-700">Quick Review HTML:</label>
        <textarea
          className="w-full border px-3 py-2 rounded h-64 font-mono text-sm"
          placeholder="<h3>Summary...</h3><ul><li>Point 1</li><li>Point 2</li></ul>"
          value={quickReviewHtml}
          onChange={(e) => setQuickReviewHtml(e.target.value)}
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <button
          onClick={handleSave}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
        >
          💾 Save Quick Review
        </button>

        <button
          onClick={handleFetchLatest}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 transition"
        >
          🔄 Fetch Latest
        </button>

        {selectedOutline && (
          <button
            onClick={() => {
              if (!status.includes('saved')) {
                Swal.fire({
                  icon: 'warning',
                  title: 'Unsaved Content',
                  text: 'Please save your changes before previewing.',
                });
                return;
              }
              window.open(`/quickreview/${selectedOutline.value}`, '_blank');
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
          >
            🔍 Show Preview
          </button>
        )}
      </div>

      {/* Status Message */}
      {status && <div className="text-sm text-indigo-700 mb-4">{status}</div>}
    </div>
  );
}
